'use strict';
import angular from 'angular';

angular.module('app.services')

    .factory('CommentService', function(CommentFactory, DisplayService, $q, $sanitize) {
        let service = {};
        service.comment = null;
        service.comments = null;

        function escapeHtml(value) {
            return value
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;');
        }

        // Reformat line breaks into br tags. Some comments (e.g. task-form
        // snapshots) embed truncated/malformed pasted HTML that textAngular's
        // $sanitize can't parse, which throws and makes ng-bind-html render the
        // whole comment blank (TO-9773). Fall back to escaped text in that case.
        function formatCommentHtml(rawComment) {
            const withBreaks = rawComment.replace(/(?:\r\n|\r|\n)/g, '<br />');
            try {
                $sanitize(withBreaks);
                return withBreaks;
            } catch {
                return escapeHtml(rawComment).replace(/(?:\r\n|\r|\n)/g, '<br />');
            }
        }

        // Get all comments for a line item
        service.getLineItemComments = function(lineItemId, searchKey) {
            let deferred = $q.defer();
            const params = {
                'tableName': 'line_item',
                'tableRowId': lineItemId,
                'search': searchKey
            };

            CommentFactory.comments().get(params).$promise.then(function(data) {
                if (data?.comments) {
                    // Reformat line breaks into br tags
                    let formattedComments = [];

                    for (let i=0; i<data.comments.length; i++) {
                        data.comments[i].comment = formatCommentHtml(data.comments[i].comment);
                        formattedComments.push(data.comments[i]);
                    }
                    service.comments = formattedComments;
                    service.comments.forEach(comment => {
                        const created_at = DisplayService.yyyymmddToShortddyyyy(comment.created_at, true);
                        const date_array = created_at?.split(',');
                        if (date_array?.length > 1) {
                            comment.created_at_mdy = date_array[0].trim();
                            comment.created_at_his = date_array[1].trim();
                        }
                    });
                } else {
                    service.comments = [];
                }
                service.comments = data.comments;
                deferred.resolve(data.comments);
            }, function(response) {
                console.log("error getting line item comments");
                deferred.reject();
            });

            return deferred.promise;
        };

        // Get all comments for a flight
        service.getFlightComments = function(flightId, searchKey) {
            let deferred = $q.defer();
            const params = {
                'tableName': 'flight',
                'tableRowId': flightId,
                'search': searchKey
            };

            CommentFactory.comments().get(params).$promise.then(function(data) {
                if (data?.comments) {
                    // Reformat line breaks into br tags
                    let formattedComments = [];

                    for (let i=0; i<data.comments.length; i++) {
                        data.comments[i].comment = formatCommentHtml(data.comments[i].comment);
                        formattedComments.push(data.comments[i]);
                    }
                    service.comments = formattedComments;
                    service.comments.forEach(comment => {
                        const created_at = DisplayService.yyyymmddToShortddyyyy(comment.created_at, true);
                        const date_array = created_at?.split(',');
                        if (date_array?.length > 1) {
                            comment.created_at_mdy = date_array[0].trim();
                            comment.created_at_his = date_array[1].trim();
                        }
                    });
                } else {
                    service.comments = [];
                }
                service.comments = data.comments;
                deferred.resolve();
            }, function(response) {
                console.log("error getting flight comments");
                deferred.reject();
            });

            return deferred.promise;
        };

        // Get all comments for an order
        service.getOrderComments = function(orderId, searchKey = "") {
            let deferred = $q.defer();
            const params = {
                'tableName': 'order',
                'tableRowId': orderId,
                'search': searchKey
            };

            CommentFactory.comments().get(params).$promise.then(function(data) {
                // Reformat line breaks into br tags
                if (data?.comments) {
                    let formattedComments = [];

                    for (let i=0; i<data.comments.length; i++) {
                        data.comments[i].comment = formatCommentHtml(data.comments[i].comment);
                        formattedComments.push(data.comments[i]);
                    }
                    service.comments = formattedComments;
                    service.comments.forEach(comment => {
                        const created_at = DisplayService.yyyymmddToShortddyyyy(comment.created_at, true);
                        const date_array = created_at?.split(',');
                        if (date_array?.length > 1) {
                            comment.created_at_mdy = date_array[0].trim();
                            comment.created_at_his = date_array[1].trim();
                        }
                    });
                } else {
                    service.comments = [];
                }
                deferred.resolve();
            }, function(response) {
                console.log("error getting order comments");
                deferred.reject();
            });

            return deferred.promise;
        };

        // Get all comments for a client
        service.getClientComments = function(clientId, searchKey = "") {
            let deferred = $q.defer();
            let params = {
                'tableName': 'client',
                'tableRowId': clientId,
                'search': searchKey
            };

            CommentFactory.comments().get(params).$promise.then(function(data) {
                // Reformat line breaks into br tags
                if (data?.comments) {
                    let formattedComments = [];

                    for (let i=0; i<data.comments.length; i++) {
                        data.comments[i].comment = formatCommentHtml(data.comments[i].comment);
                        formattedComments.push(data.comments[i]);
                    }
                    service.comments = formattedComments;
                    service.comments.forEach(comment => {
                        const created_at = DisplayService.yyyymmddToShortddyyyy(comment.created_at, true);
                        const date_array = created_at?.split(',');
                        if (date_array?.length > 1) {
                            comment.created_at_mdy = date_array[0].trim();
                            comment.created_at_his = date_array[1].trim();
                        }
                    });
                } else {
                    service.comments = [];
                }
                deferred.resolve();
            }, function(response) {
                console.log("error getting client comments");
                deferred.reject();
            });

            return deferred.promise;
        };

        // Add a comment
        service.addComment = function(entityType, entityId, comment, skipWorkflow,
                                      addOnDemandTask, onDemandTask, taskAssignmentId) {
            let deferred = $q.defer();
            let params = {
                'tableName': entityType,
                'tableRowId': entityId,
                'comment': comment,
                'addOnDemandTask': addOnDemandTask,
                'onDemandTask': onDemandTask,
                'taskAssignmentId': taskAssignmentId
            };
            params.skipWorkflow = skipWorkflow ? '1' : '0';

            CommentFactory.comment().save(params).$promise.then(function(data) {
                service.comment = data.comment;
                deferred.resolve();
            }, function(response) {
                console.log("error adding new comment");
                deferred.reject();
            });

            return deferred.promise;
        }

        // Add a single line item comment
        service.addLineItemComment = function(params) {
            let deferred = $q.defer();

            CommentFactory.comment().save(params).$promise.then(function(data) {
                service.comment = data.comment;
                deferred.resolve();
            }, function(response) {
                console.log("error adding new comment");
                deferred.reject();
            });

            return deferred.promise;
        };

        // Add a single flight comment
        service.addFlightComment = function(params) {
            let deferred = $q.defer();

            CommentFactory.comment().save(params).$promise.then(function(data) {
                service.comment = data.comment;
                deferred.resolve();
            }, function(response) {
                console.log("error adding new flight comment");
                deferred.reject();
            });

            return deferred.promise;
        };

        // Add a single client comment
        service.addClientComment = function(clientId, comment, skipWorkflow, addOnDemandTask, onDemandTask) {
            let deferred = $q.defer();
            let params = {
                'tableName': 'client',
                'tableRowId': clientId,
                'comment': comment,
                'addOnDemandTask': addOnDemandTask,
                'onDemandTask': onDemandTask
            };

            if (skipWorkflow) {
                params.skipWorkflow = '1';
            }

            CommentFactory.comment().save(params).$promise.then(function(data) {
                service.comment = data.comment;
                deferred.resolve();
            }, function(response) {
                console.log("error adding new client comment");
                deferred.reject();
            });

            return deferred.promise;
        };

        // Add a single order comment
        service.addOrderComment = function(params) {
            let deferred = $q.defer();

            CommentFactory.comment().save(params).$promise.then(function(data) {
                service.comment = data.comment;
                deferred.resolve();
            }, function(response) {
                console.log("error adding new order comment");
                deferred.reject();
            });

            return deferred.promise;
        };

        // Delete a single comment
        service.deleteComment = function(commentId) {
            let deferred = $q.defer();

            CommentFactory.comment().delete({
                'id': commentId
            }).$promise.then(function(data) {
                deferred.resolve(data);
            }, function(response) {
                console.log("error deleting comment");
                deferred.reject();
            });

            return deferred.promise;
        };

        // Add a single comment on multiple tasks
        service.addCommentOnTasks = function(taskAssignmentIds, comment) {
            let deferred = $q.defer();
            const params = {
                'taskAssignmentIds': taskAssignmentIds,
                'comment': comment
            };

            CommentFactory.addCommentOnTasks().save(params).$promise.then(function(data) {
                service.comment = data.comment;
                deferred.resolve();
            }, function(response) {
                console.log("error adding new comment");
                deferred.reject();
            });

            return deferred.promise;
        };

        service.getLineItemFlightsComments = function(lineItemId) {
            let deferred = $q.defer();
            const params = {
                'lineItemId': lineItemId,
            };

            CommentFactory.lineItemFlightsComments().get(params).$promise.then(function(data) {
                if (data?.comments) {
                    // Reformat line breaks into br tags
                    let formattedComments = [];

                    for (let i=0; i<data.comments.length; i++) {
                        data.comments[i].comment = formatCommentHtml(data.comments[i].comment);
                        formattedComments.push(data.comments[i]);
                    }
                    service.comments = formattedComments;
                    service.comments.forEach(comment => {
                        const created_at = DisplayService.yyyymmddToShortddyyyy(comment.created_at, true);
                        const date_array = created_at?.split(',');
                        if (date_array?.length > 1) {
                            comment.created_at_mdy = date_array[0].trim();
                            comment.created_at_his = date_array[1].trim();
                        }
                    });
                } else {
                    service.comments = [];
                }
                deferred.resolve(service.comments);
            }, function(response) {
                console.log("error getting flight comments");
                deferred.reject();
            });

            return deferred.promise;
        };

        return service;
    })

    .factory('CommentFactory', ['$resource', 'AppConfig', function($resource, AppConfig) {
        let factory = {};

        factory.comments = function() {
            return $resource(AppConfig.IO_API_URL + 'comments', null, {
                'get': {
                    method: 'GET',
                    isArray: false
                }
            });
        };

        factory.comment = function() {
            return $resource(AppConfig.IO_API_URL + 'comments/comment', null, {
                'get': {
                    method: 'GET',
                    isArray: false
                },
                'save': {
                    method: 'POST',
                    isArray: false
                },
                'delete': {
                    method: 'DELETE',
                    isArray: false
                }
            });
        };

        factory.toggleCommentState = function() {
            return $resource(AppConfig.IO_API_URL + 'comments/toggleCommentState', null, {
                'update': {
                    method: 'PUT'
                }
            });
        };

        factory.addCommentOnTasks = function() {
            return $resource(AppConfig.IO_API_URL + 'comments/addCommentOnTasks', null, {
                'save': {
                    method: 'POST',
                    isArray: false
                }
            });
        };

        factory.lineItemFlightsComments = function() {
            return $resource(AppConfig.IO_API_URL + 'comments/lineItemFlightsComments', null, {
                'get': {
                    method: 'GET',
                    isArray: false
                }
            });
        };

        return factory;
    }]);
